import React, { useCallback, useEffect, useMemo, useState } from 'react'

import classNames from 'classnames'
import moment from 'moment'
import 'moment/locale/sk'
import { Calendar, EventProps, momentLocalizer, Views } from 'react-big-calendar'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useLocalStorage } from 'usehooks-ts'

import AppLayout from '../common/AppLayout'
import AppTitle from '../common/AppTitle'
import { fetchClients } from '../common/helpers'
import axiosClient from '../service/axiosClient'
import { CalendarComponentEvent, CalendarEvent, CalendarEventType } from '../types/Calendar'
import ClientDTO from '../types/Client'
import { PageableResponse } from '../types/Common'


const localizer = momentLocalizer(moment)

// How long an event with no end is drawn for, so it stays visible and clickable
const OPEN_ENDED_EVENT_MINUTES = 30

function EventComponent({event}: EventProps<CalendarComponentEvent>) {
    return <span>{event.title}</span>
}

function eventStyleGetter(event: CalendarComponentEvent) {
    return {
        style: {
            backgroundColor: event.color || '#3A3E5C',
            color: '#fff',
            borderRadius: '4px',
            border: 'none',
        }
    }
}

const CustomToolbar = (props: any) => {
    // Získanie lokalizovaných textov
    const {messages} = props.localizer

    const goToBack = () => {
        props.onNavigate('PREV')
    }

    const goToNext = () => {
        props.onNavigate('NEXT')
    }

    const goToToday = () => {
        props.onNavigate('TODAY')
    }

    function view(view: any) {
        props.onView(view)
    }

    function viewNamesGroup(messages: any) {
        const viewNames = props.views
        // const view = props.view

        if (viewNames.length > 1) {
            return viewNames.map((name: any) => (
                <button
                    type="button"
                    key={name}
                    className={classNames('btn amani-primary pink-btn small', {'rbc-active': view === name})}
                    onClick={
                        () => view(name)}
                >
                    {messages[name]}
                </button>
            ))
        }
    }

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            position: 'relative'
        }}>
            <button onClick={goToToday} className={'btn amani-primary pink-btn small'}>{messages.today}</button>
            {/*<button onClick={goToBack}>{messages.previous}</button>*/}
            <button type="button" onClick={goToBack} style={{position: 'initial'}}
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                    aria-label={messages.previous}><span
                className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{messages.previous}</span>
            </button>

            <span className={'rbc-toolbar-label'} style={{margin: 0}}>{props.label}</span> {/* Lokalizovaný názov mesiaca/roku */}
            {/*<button onClick={goToNext}>{messages.next}</button>*/}
            <button type="button" className="react-datepicker__navigation react-datepicker__navigation--next"
                    style={{position: 'initial'}}
                    aria-label={messages.next} onClick={goToNext}><span
                className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{messages.next}</span>
            </button>

            <span className="rbc-btn-group">{viewNamesGroup(messages)}</span>

        </div>
    )
}

function CalendarPage() {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const [eventTypes, setEventTypes] = useState<CalendarEventType[]>([])
    const [myEvents, setEvents] = useState<CalendarComponentEvent[]>([])
    const [clients, setClients] = useState<ClientDTO[]>([])
    const [selectedClient, setSelectedClient] = useLocalStorage<ClientDTO | null>('calendar-client', null)
    const [selectedType, setSelectedType] = useLocalStorage<CalendarEventType | null>('calendar-event-type', null)

    useEffect(() => {
        axiosClient.get('calendar-event/type').then(response => {
            console.log('response types', response)
            setEventTypes(response.data)
        }).catch(error => {
            console.log('error', error)
        })
        fetchClients().then(setClients).catch(console.error)
    }, [])

    useEffect(() => {
        // if (selectedClient && selectedClient?.id) {
        axiosClient.get<PageableResponse<CalendarEvent>>('calendar-event', {
            params: {
                clientId: selectedClient?.id,
                pageSize: 200,
                pageStart: 0
            }
        })
            .then(response => {
                console.log('res', response)
                const eventsTmp: CalendarComponentEvent[] = []
                response.data.content.forEach(item => {
                    // All-day means no time at all. An event that starts at a time but says
                    // nothing about its end is still a timed event, not an all-day one.
                    const isAllDay = !item.startedAt
                    // Check if multi-day event
                    const isMultiDay = !!(item.entryDateEnd && item.entryDateEnd !== item.entryDate)
                    let start: Date
                    let end: Date

                    if (isMultiDay) {
                        // Multi-day event: spans from entryDate to entryDateEnd
                        start = new Date(item.entryDate + 'T' + (item.startedAt || '00:00:00'))
                        end = new Date(item.entryDateEnd + 'T' + (item.endedAt || '23:59:59'))
                    } else if (isAllDay) {
                        // All-day event: use just the date, set to start of day
                        start = new Date(item.entryDate + 'T00:00:00')
                        end = new Date(item.entryDate + 'T23:59:59')
                    } else {
                        start = new Date(item.entryDate + 'T' + item.startedAt)
                        // Without an end there is no duration to draw, so it is shown as a short
                        // block. Nothing is stored for it - the event stays open-ended.
                        end = item.endedAt
                            ? new Date(item.entryDate + 'T' + item.endedAt)
                            : new Date(start.getTime() + OPEN_ENDED_EVENT_MINUTES * 60 * 1000)
                    }

                    if ((selectedType && selectedType.id === item.eventType?.id) || !selectedType) {
                        eventsTmp.push({
                            id: item.id as number,
                            title: item.name,
                            start: start,
                            end: end,
                            color: item.color,
                            allDay: isAllDay || isMultiDay
                        })
                    }
                })
                console.log('eventsTmp', eventsTmp)
                setEvents(eventsTmp)
            }).catch(error => {
            console.log('error', error)
        })
        // }
    }, [selectedClient, selectedType])

    const handleSelectSlot = useCallback(
        ({start, end}: { start: Date, end: Date }) => {
            console.log('{start, end}', {start, end})
            console.log('selectedClient', selectedClient)
            // navigate(`/calendar/add?clientId=${selectedClient?.id}&start=${start.toDateString()}&end=${end.toDateString()}`)
            navigate(`/app/calendar/add?clientId=${selectedClient?.id || ''}&entryDate=${start.getFullYear() + '-' + ('0' + (start.getMonth() + 1)).slice(-2) + '-' + ('0' + start.getDate()).slice(-2)}`)
            // const title = window.prompt('New Event Name')
            // if (title) {
            //     //@ts-ignore
            //     setEvents((prev) => [...prev, {start, end, title}])
            // }
        },
        [selectedClient, selectedType]
    )

    const handleSelectEvent = useCallback((event: CalendarComponentEvent) => {
        navigate('/app/calendar/edit/' + event.id)
    }, [])

    const {defaultDate, scrollToTime} = useMemo(
        () => ({
            defaultDate: new Date(),
            scrollToTime: new Date(1970, 1, 1, 6),
        }),
        []
    )


    return (
        <AppLayout>

            <AppTitle className={'d-flex'} append={<Link to={`/app/calendar/add?clientId=${selectedClient?.id || ''}`}>
                <button className="btn amani-primary">{t('new_event')}</button>
            </Link>}>
                {t('Kalendár')}
            </AppTitle>

            <div className="rounded-content-body mb-4">
                <div className="row d-flex justify-content-center">
                    <div className="col-md-4">
                        <div
                            className={'form-group'}>
                            <label htmlFor="clientId">
                                {t('client')}
                            </label>
                            <select
                                id="clientId"
                                name="clientId"
                                className="form-select"
                                onChange={(e) => {
                                    setSelectedClient(clients.find(client => client.id === parseInt(e.target.value)) || null)
                                    // setFilter((old) => ({...old, clientId: e.target.value}))
                                }}>
                                <option value="">{t('select')}</option>
                                {clients.map((option) => (
                                    <option key={option.id}
                                            selected={selectedClient?.id === option.id}
                                            value={option.id}>{option.companyName || (`${option.firstName} ${option.lastName}`)}</option>))}
                            </select>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div
                            className={'form-group'}>
                            <label htmlFor="eventType">
                                {t('event_type')}
                            </label>
                            <select id="eventType" name="eventType"
                                    value={selectedType?.id}
                                    onChange={(e) => {
                                        const eventType = eventTypes.find(et => et.id.toString() === e.target.value)
                                        console.log('eventType', eventType)
                                        setSelectedType(eventType || null)
                                    }}
                                    className="form-select">
                                <option value="">{t('select')}</option>
                                {eventTypes.map((option) => (
                                    <option key={option.id}
                                            value={option.id}>{option.name}</option>))}
                            </select>
                        </div>
                    </div>
                    <div className="col-md-4">
                    </div>

                </div>
            </div>

            <div className="rounded-content-body p-4">
                <Calendar
                    defaultDate={defaultDate}
                    defaultView={Views.MONTH}
                    events={myEvents}
                    localizer={localizer}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable
                    components={{
                        event: EventComponent,
                        toolbar: CustomToolbar,
                    }}
                    eventPropGetter={eventStyleGetter}
                    scrollToTime={scrollToTime}
                    messages={{
                        next: t('calendar_next'),
                        previous: t('calendar_previous'),
                        today: t('calendar_today'),
                        month: t('calendar_month'),
                        week: t('calendar_week'),
                        day: t('calendar_day'),
                        time: t('time'),
                        event: t('calendar_event_label'),
                        allDay: t('all_day'),
                        showMore: (count) => t('calendar_show_more'),
                        date: t('date'),
                    }}
                />
            </div>
        </AppLayout>
    )
}

export default CalendarPage
